<template>
  <div>
    <v-row dense class="mb-4">
      <v-col cols="12" sm="4">
        <v-card class="stock-invoice-stat-card stock-invoice-stat-card--draft" outlined>
          <v-card-text class="d-flex align-center py-4">
            <v-avatar color="grey lighten-3" size="48" class="mr-4">
              <v-icon color="grey darken-1">mdi-file-edit-outline</v-icon>
            </v-avatar>
            <div>
              <div class="caption grey--text text--darken-1">{{ $t("STOCK.INVOICES.STAT_DRAFT") }}</div>
              <div class="text-h5 font-weight-bold">{{ stats.draftCount }}</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="4">
        <v-card class="stock-invoice-stat-card stock-invoice-stat-card--issued" outlined>
          <v-card-text class="d-flex align-center py-4">
            <v-avatar color="teal lighten-5" size="48" class="mr-4">
              <v-icon color="teal darken-2">mdi-check-circle-outline</v-icon>
            </v-avatar>
            <div>
              <div class="caption grey--text text--darken-1">{{ $t("STOCK.INVOICES.STAT_ISSUED") }}</div>
              <div class="text-h5 font-weight-bold">{{ stats.issuedCount }}</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="4">
        <v-card class="stock-invoice-stat-card stock-invoice-stat-card--gross" outlined>
          <v-card-text class="d-flex align-center py-4">
            <v-avatar color="amber lighten-4" size="48" class="mr-4">
              <v-icon color="amber darken-3">mdi-currency-eur</v-icon>
            </v-avatar>
            <div>
              <div class="caption grey--text text--darken-1">{{ $t("STOCK.INVOICES.STAT_GROSS") }}</div>
              <div class="text-h5 font-weight-bold">{{ formatMoney(stats.totalGross) }}</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <div class="d-flex flex-wrap align-center mb-3">
      <span class="caption grey--text text--darken-1 mr-2">{{ $t("STOCK.INVOICES.FILTER_DIRECTION") }}:</span>
      <v-chip-group v-model="directionFilter" mandatory active-class="primary--text" class="stock-invoice-direction-chips">
        <v-chip filter value="" small>{{ $t("GENERAL.LABELS.ALL") }}</v-chip>
        <v-chip filter value="outgoing" small color="teal" outlined>
          <v-icon left x-small>mdi-arrow-right-bold</v-icon>
          {{ $t("STOCK.INVOICES.DIRECTIONS.OUTGOING") }}
        </v-chip>
        <v-chip filter value="incoming" small color="deep-purple" outlined>
          <v-icon left x-small>mdi-arrow-left-bold</v-icon>
          {{ $t("STOCK.INVOICES.DIRECTIONS.INCOMING") }}
        </v-chip>
      </v-chip-group>
    </div>

    <v-card :disabled="isProcessing" :loading="isProcessing">
      <template v-slot:progress>
        <v-progress-linear absolute color="success" height="4" indeterminate></v-progress-linear>
      </template>
      <v-tabs v-model="tab" class="stock-color" left dark icons-and-text>
        <v-tabs-slider></v-tabs-slider>
        <v-tab href="#tab-1">
          <v-toolbar-title>{{ $t("STOCK.INVOICES.TITLE") }}</v-toolbar-title>
        </v-tab>
        <v-spacer></v-spacer>
        <div class="buttons-tab">
          <v-btn
            v-if="hasPermissions('stock-invoices-create')"
            class="mr-4 success"
            :disabled="isProcessing"
            @click="showCreateDialog = true"
          >
            {{ $t("GENERAL.LABELS.CREATE_BUTTON") }}
          </v-btn>
        </div>
      </v-tabs>
      <v-tabs-items v-model="tab">
        <v-tab-item value="tab-1">
          <v-card flat>
            <v-card-text class="pa-0">
              <Table
                v-if="userStatePrepared"
                ref="stock-invoices-table"
                height="500"
                name="stock-invoices-table"
                :headers="headers"
                :blink-row-item-key="blinkRowItemKey"
                :server-data-options="{ method: tableItemsMethod, params: tableExtraParams }"
                :items-per-page="25"
                :loading="isProcessing"
                :filters="tableFilters"
                dense
                fixed-header
                filterable
                @filters-change="onFiltersChange"
                @search="onSearchChange"
              >
                <template v-slot:top-header-actions>
                  <v-btn
                    icon
                    color="indigo"
                    :loading="isExporting"
                    :disabled="isProcessing || isExporting"
                    @click="exportInvoices"
                  >
                    <v-tooltip top color="primary">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on">mdi-file-excel</v-icon>
                      </template>
                      <span>{{ $t("STOCK.INVOICES.EXPORT_EXCEL") }}</span>
                    </v-tooltip>
                  </v-btn>
                  <v-spacer></v-spacer>
                </template>
                <template
                  v-for="(header, index) in headers"
                  v-slot:[`item.${header.value}`]="{ item }"
                >
                  <template v-if="header.value === 'fiscal_number'">
                    <router-link
                      :key="index"
                      :to="{ name: 'stock-invoices-view', params: { id: item.item.id } }"
                    >
                      {{ documentNumberLabel(item.item) }}
                    </router-link>
                  </template>
                  <template v-else-if="header.value === 'code'">
                    <router-link
                      :key="index"
                      :to="{ name: 'stock-invoices-view', params: { id: item.item.id } }"
                    >
                      {{ item.item.code }}
                    </router-link>
                  </template>
                  <template v-else-if="header.value === 'status'">
                    <v-chip
                      :key="index"
                      small
                      :class="`stock-invoice-status--${item.item.status}`"
                      dark
                    >
                      {{ statusLabel(item.item.status) }}
                    </v-chip>
                  </template>
                  <template v-else-if="header.value === 'direction'">
                    <v-chip
                      :key="index"
                      small
                      :color="directionColor(item.item.direction)"
                      dark
                    >
                      {{ directionLabel(item.item.direction) }}
                    </v-chip>
                  </template>
                  <template v-else-if="header.value === 'type'">
                    {{ typeLabel(item.item.type) }}
                  </template>
                  <template v-else-if="header.value === 'source_invoice'">
                    <router-link
                      v-if="item.item.source_invoice"
                      :key="index"
                      :to="{ name: 'stock-invoices-view', params: { id: item.item.source_invoice.id } }"
                    >
                      {{ sourceDocumentLabel(item.item.source_invoice) }}
                    </router-link>
                    <span v-else :key="index">-</span>
                  </template>
                  <template v-else-if="header.value === 'company.label'">
                    {{ companyLabel(item.item) }}
                  </template>
                  <template v-else-if="header.value === 'delivery_notes'">
                    <div v-if="deliveryNotesOf(item.item).length" :key="index" class="invoice-dn-links">
                      <router-link
                        v-for="note in deliveryNotesOf(item.item)"
                        :key="note.id"
                        :to="{ name: 'stock-delivery-notes-view', params: { id: note.id } }"
                      >
                        {{ note.code }}
                      </router-link>
                    </div>
                    <span v-else :key="index">-</span>
                  </template>
                  <template v-else-if="header.value === 'organization.name'">
                    {{ organizationLabel(item.item) }}
                  </template>
                  <template v-else-if="header.value === 'total_gross'">
                    {{ formatMoney(item.item.total_gross, item.item.currency_code) }}
                  </template>
                  <template v-else-if="header.value === 'reminder_due'">
                    <v-chip
                      v-if="item.item.reminder_due"
                      :key="index"
                      small
                      color="amber darken-2"
                      dark
                    >
                      {{ $t("STOCK.INVOICES.REMINDER_DUE") }}
                    </v-chip>
                    <span v-else :key="index">-</span>
                  </template>
                  <template v-else-if="header.value === 'due_date'">
                    <span :key="index">
                      <template v-if="item.item.due_date">{{ formatDueDate(item.item.due_date) }}</template>
                      <span
                        v-if="daysUntilDue(item.item) !== null"
                        :class="{ 'red--text font-weight-bold': isOverdue(item.item) }"
                      >
                        ({{ daysUntilDue(item.item) }})
                      </span>
                      <template v-else-if="!item.item.due_date">-</template>
                    </span>
                  </template>
                  <template v-else>
                    {{ item.value || "-" }}
                  </template>
                </template>
              </Table>
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <v-dialog v-model="showCreateDialog" max-width="560" persistent>
      <v-card class="stock-invoice-create-dialog">
        <v-card-title class="stock-invoice-create-dialog__title">
          <v-icon left color="teal darken-1">mdi-file-plus-outline</v-icon>
          {{ $t("STOCK.INVOICES.CREATE_MANUAL") }}
        </v-card-title>
        <v-card-text>
          <div class="caption grey--text text--darken-1 mb-2">{{ $t("STOCK.INVOICES.DIRECTION") }}</div>
          <v-item-group v-model="createForm.direction" mandatory class="mb-4">
            <v-row dense>
              <v-col cols="6">
                <v-item v-slot="{ active, toggle }" value="outgoing">
                  <v-card
                    :class="['stock-invoice-direction-card', { 'stock-invoice-direction-card--active': active }]"
                    outlined
                    @click="toggle"
                  >
                    <v-card-text class="text-center py-3 px-2">
                      <v-icon :color="active ? 'teal darken-1' : 'grey'">mdi-arrow-right-bold</v-icon>
                      <div class="caption mt-1">{{ $t("STOCK.INVOICES.DIRECTIONS.OUTGOING") }}</div>
                    </v-card-text>
                  </v-card>
                </v-item>
              </v-col>
              <v-col cols="6">
                <v-item v-slot="{ active, toggle }" value="incoming">
                  <v-card
                    :class="['stock-invoice-direction-card', { 'stock-invoice-direction-card--active-incoming': active }]"
                    outlined
                    @click="toggle"
                  >
                    <v-card-text class="text-center py-3 px-2">
                      <v-icon :color="active ? 'deep-purple darken-1' : 'grey'">mdi-arrow-left-bold</v-icon>
                      <div class="caption mt-1">{{ $t("STOCK.INVOICES.DIRECTIONS.INCOMING") }}</div>
                    </v-card-text>
                  </v-card>
                </v-item>
              </v-col>
            </v-row>
          </v-item-group>
          <v-autocomplete
            v-model="createForm.organization_id"
            :items="organizations"
            item-text="label"
            item-value="id"
            :label="$t('STOCK.WAREHOUSES.ORGANIZATION') + '*'"
            class="mb-3"
            outlined
            dense
          ></v-autocomplete>
          <v-autocomplete
            v-model="createForm.type"
            :items="typeOptions"
            item-text="label"
            item-value="slug"
            :label="$t('STOCK.INVOICES.TYPE') + '*'"
            class="mb-3"
            outlined
            dense
          ></v-autocomplete>
          <v-autocomplete
            v-model="createForm.company_id"
            :items="companyOptions"
            :search-input.sync="companySearch"
            item-text="label"
            item-value="id"
            :label="$t('STOCK.INVOICES.COMPANY') + '*'"
            outlined
            dense
            clearable
            class="mb-3"
          ></v-autocomplete>
          <v-text-field
            v-if="createForm.direction === 'incoming'"
            v-model="createForm.external_number"
            :label="$t('STOCK.INVOICES.SUPPLIER_INVOICE_NUMBER') + '*'"
            :error-messages="createValidationErrors.external_number || []"
            outlined
            dense
            @input="clearCreateValidationError('external_number')"
          ></v-text-field>
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <v-spacer></v-spacer>
          <v-btn text @click="showCreateDialog = false">{{ $t("GENERAL.LABELS.CANCEL") }}</v-btn>
          <v-btn
            color="success"
            :loading="isCreating"
            :disabled="!canSubmitCreate"
            @click="createManualInvoice"
          >
            <v-icon left small>mdi-arrow-right</v-icon>
            {{ $t("GENERAL.LABELS.CREATE_BUTTON") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Table from "@/views/partials/v-table/Table.vue";
import userStateService from "@/common/userState.service";
import { SHOW_SNACK } from "@/store/snack.module";
import { GET_ORGANIZATIONS_OPTIONS } from "@/store/organizations.module";
import { GET_COMPANIES_OPTIONS } from "@/store/companies.module";
import { GET_STOCK_DEAL_OPTIONS } from "@/store/stock/stockReceipts.module";
import {
  GET_STOCK_INVOICES,
  EXPORT_STOCK_INVOICES,
  GET_STOCK_INVOICE_TYPES,
  GET_STOCK_INVOICE_DIRECTIONS,
  CREATE_STOCK_INVOICE,
} from "@/store/stock/stockInvoices.module";

export default {
  name: "StockListInvoices",
  components: { Table },
  data() {
    return {
      tableName: "stock-invoices-table",
      blinkRowItemKey: null,
      userStatePrepared: false,
      tab: "tab-1",
      isProcessing: false,
      isExporting: false,
      isCreating: false,
      showCreateDialog: false,
      createValidationErrors: {},
      directionFilter: "",
      activeTableFilters: {},
      activeTableSearch: "",
      statsRequestId: 0,
      tableItemsMethod: GET_STOCK_INVOICES,
      tableFilters: [],
      stats: { draftCount: 0, issuedCount: 0, totalGross: 0 },
      typeOptions: [],
      directionOptions: [],
      organizations: [],
      companyOptions: [],
      companySearch: "",
      createForm: {
        organization_id: null,
        direction: "outgoing",
        type: "invoice",
        company_id: null,
        external_number: "",
      },
      headers: [
        { text: this.$t("STOCK.INVOICES.DOCUMENT_NUMBER"), value: "fiscal_number", sortable: true, align: "center", width: "9%" },
        { text: this.$t("STOCK.INVOICES.INTERNAL_REF"), value: "code", sortable: true, align: "center", width: "8%" },
        { text: this.$t("STOCK.INVOICES.SOURCE_DOCUMENT"), value: "source_invoice", sortable: false, align: "center", width: "10%" },
        { text: this.$t("GENERAL.LABELS.STATUS"), value: "status", sortable: true, align: "center", width: "8%" },
        { text: this.$t("STOCK.INVOICES.DIRECTION"), value: "direction", sortable: false, align: "center", width: "8%" },
        { text: this.$t("STOCK.INVOICES.TYPE"), value: "type", sortable: false, align: "center", width: "8%" },
        { text: this.$t("STOCK.INVOICES.COMPANY"), value: "company.label", sortable: false, align: "center", width: "12%" },
        { text: this.$t("STOCK.DELIVERY_NOTES.TITLE"), value: "delivery_notes", sortable: false, align: "center", width: "10%" },
        { text: this.$t("STOCK.WAREHOUSES.ORGANIZATION"), value: "organization.name", sortable: false, align: "center", width: "9%" },
        { text: this.$t("STOCK.INVOICES.ISSUE_DATE"), value: "issue_date", sortable: true, align: "center", width: "8%" },
        { text: this.$t("STOCK.INVOICES.DUE_DATE"), value: "due_date", sortable: true, align: "center", width: "10%" },
        { text: this.$t("STOCK.INVOICES.REMINDER"), value: "reminder_due", sortable: false, align: "center", width: "8%" },
        { text: this.$t("STOCK.INVOICES.GROSS"), value: "total_gross", sortable: false, align: "center", width: "8%" },
      ],
    };
  },
  computed: {
    stockContextVersion() {
      return this.$store.getters.stockContextVersion;
    },
    tableExtraParams() {
      const params = {};
      if (this.directionFilter) {
        params.filters = { ...(params.filters || {}), direction: [this.directionFilter] };
      }
      return params;
    },
    canSubmitCreate() {
      return (
        this.createForm.organization_id &&
        this.createForm.direction &&
        this.createForm.type &&
        this.createForm.company_id &&
        (this.createForm.direction !== "incoming" ||
          !!(this.createForm.external_number || "").trim())
      );
    },
  },
  watch: {
    stockContextVersion() {
      this.loadTableFilters().finally(() => {
        this.reloadTable();
        this.loadStats();
      });
    },
    directionFilter() {
      this.reloadTable();
      this.loadStats();
    },
    companySearch() {
      this.loadCompanies();
    },
    showCreateDialog(open) {
      if (open) {
        this.createValidationErrors = {};
        if (this.directionFilter) {
          this.createForm.direction = this.directionFilter;
        }
        this.loadCreateOptions();
      }
    },
    "createForm.direction"() {
      this.clearCreateValidationError("external_number");
      if (this.createForm.direction !== "incoming") {
        this.createForm.external_number = "";
      }
    },
  },
  mounted() {
    this.blinkRowItemKey = userStateService.getState(
      `table-state.${this.tableName}.blinkRowItemKey`,
      null
    );
    Promise.all([
      this.loadLookupOptions(),
      this.loadTableFilters(),
    ]).finally(() => {
      this.userStatePrepared = true;
      this.loadStats();
    });
  },
  methods: {
    tableExtraParamsForStats(extraFilters = {}) {
      const params = { page: 1, itemsPerPage: 1 };
      const filters = {
        ...this.activeTableFilters,
        ...extraFilters,
      };
      if (this.directionFilter) {
        filters.direction = [this.directionFilter];
      }
      const search = (this.activeTableSearch || "").trim();
      if (search) {
        params.search = search;
      }
      if (Object.keys(filters).length) {
        params.filters = filters;
      }
      return params;
    },
    async loadStats() {
      const requestId = ++this.statsRequestId;

      try {
        const baseParams = this.tableExtraParamsForStats();
        const [draftRes, issuedRes, grossRes] = await Promise.all([
          this.$store.dispatch(GET_STOCK_INVOICES, {
            ...baseParams,
            itemsPerPage: 1,
            filters: { ...(baseParams.filters || {}), status: ["draft"] },
          }),
          this.$store.dispatch(GET_STOCK_INVOICES, {
            ...baseParams,
            itemsPerPage: 1,
            filters: { ...(baseParams.filters || {}), status: ["issued", "partially_paid", "paid"] },
          }),
          this.$store.dispatch(GET_STOCK_INVOICES, baseParams),
        ]);
        if (requestId !== this.statsRequestId) {
          return;
        }
        const filteredTotalGross = grossRes.meta?.filtered_total_gross;
        const grossItems = grossRes.data || [];
        this.stats = {
          draftCount: draftRes.meta?.total || 0,
          issuedCount: issuedRes.meta?.total || 0,
          totalGross: filteredTotalGross !== undefined
            ? Number(filteredTotalGross || 0)
            : grossItems.reduce((sum, row) => sum + Number(row.total_gross || 0), 0),
        };
      } catch {
        if (requestId === this.statsRequestId) {
          this.stats = { draftCount: 0, issuedCount: 0, totalGross: 0 };
        }
      }
    },
    onFiltersChange(filters = {}) {
      this.activeTableFilters = filters || {};
      this.loadStats();
    },
    onSearchChange(search = "") {
      this.activeTableSearch = search || "";
      this.loadStats();
    },
    exportLabelMap(values, labelFn) {
      return values.reduce((map, value) => {
        map[value] = labelFn.call(this, value);
        return map;
      }, {});
    },
    exportInvoices() {
      const storedSearch = userStateService.getState(
        `table-state.${this.tableName}.searchString`,
        ""
      );
      const filters = {
        ...(userStateService.getState(`table-state.${this.tableName}.serverFilters`, {}) || {}),
      };
      if (this.directionFilter) {
        filters.direction = [this.directionFilter];
      }

      const params = {
        filters,
        headings: [
          this.$t("STOCK.INVOICES.DOCUMENT_NUMBER"),
          this.$t("STOCK.INVOICES.INTERNAL_REF"),
          this.$t("STOCK.INVOICES.SOURCE_DOCUMENT"),
          this.$t("GENERAL.LABELS.STATUS"),
          this.$t("STOCK.INVOICES.DIRECTION"),
          this.$t("STOCK.INVOICES.TYPE"),
          this.$t("STOCK.INVOICES.COMPANY"),
          this.$t("STOCK.WAREHOUSES.ORGANIZATION"),
          this.$t("STOCK.INVOICES.ISSUE_DATE"),
          this.$t("STOCK.INVOICES.DUE_DATE"),
          this.$t("STOCK.INVOICES.GROSS"),
        ],
        labels: {
          status: this.exportLabelMap(
            ["draft", "issued", "partially_paid", "paid", "cancelled"],
            this.statusLabel
          ),
          type: this.exportLabelMap(
            ["invoice", "proforma", "credit_note", "debit_note"],
            this.typeLabel
          ),
          direction: this.exportLabelMap(["outgoing", "incoming"], this.directionLabel),
        },
      };
      const search = (storedSearch || "").trim();
      if (search.length > 2) {
        params.search = search;
      }

      this.isExporting = true;
      this.$store.dispatch(SHOW_SNACK, {
        message: this.$t("STOCK.INVOICES.EXPORT_STARTED"),
        type: "warning",
        timeout: 10000,
      });

      this.$store
        .dispatch(EXPORT_STOCK_INVOICES, params)
        .then(() => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.$t("STOCK.INVOICES.EXPORT_COMPLETED"),
            type: "success",
          });
        })
        .catch(() => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.$t("GENERAL.UNEXPECTED_ERROR"),
            type: "error",
          });
        })
        .finally(() => {
          this.isExporting = false;
        });
    },
    async loadLookupOptions() {
      try {
        const [types, directions] = await Promise.all([
          this.$store.dispatch(GET_STOCK_INVOICE_TYPES),
          this.$store.dispatch(GET_STOCK_INVOICE_DIRECTIONS),
        ]);
        this.typeOptions = (types || []).map((item) => ({
          slug: item.slug,
          label: this.typeLabel(item.slug) || item.name,
        }));
        this.directionOptions = (directions || []).map((item) => ({
          slug: item.slug,
          label: this.directionLabel(item.slug) || item.name,
        }));
      } catch {
        this.typeOptions = [
          { slug: "invoice", label: this.$t("STOCK.INVOICES.TYPES.INVOICE") },
          { slug: "proforma", label: this.$t("STOCK.INVOICES.TYPES.PROFORMA") },
        ];
        this.directionOptions = [
          { slug: "outgoing", label: this.$t("STOCK.INVOICES.DIRECTIONS.OUTGOING") },
          { slug: "incoming", label: this.$t("STOCK.INVOICES.DIRECTIONS.INCOMING") },
        ];
      }
    },
    async loadCreateOptions() {
      const orgIds = this.$store.getters.stockContextOrganizationIds || [];
      const orgParams = orgIds.length ? { ids: orgIds } : {};
      const orgs = await this.$store.dispatch(GET_ORGANIZATIONS_OPTIONS, orgParams);
      this.organizations = (orgs.data || orgs || []).map((org) => ({
        id: org.id,
        label: org.name || org.code,
      }));
      if (!this.createForm.organization_id && this.organizations.length === 1) {
        this.createForm.organization_id = this.organizations[0].id;
      }
      await this.loadCompanies();
    },
    async loadCompanies() {
      try {
        const data = await this.$store.dispatch(GET_COMPANIES_OPTIONS, {
          search: this.companySearch || undefined,
          limit: 50,
        });
        this.companyOptions = data.data || data || [];
      } catch {
        this.companyOptions = [];
      }
    },
    loadTableFilters() {
      const statusItems = ["draft", "issued", "partially_paid", "paid", "cancelled"].map((status) => ({
        id: status,
        label: this.statusLabel(status),
      }));

      const typeItems = ["invoice", "proforma", "credit_note", "debit_note"].map((type) => ({
        id: type,
        label: this.typeLabel(type),
      }));

      const organizationIds = this.$store.getters.stockContextOrganizationIds;
      const dealParams = { limit: 100 };
      if (organizationIds.length === 1) {
        dealParams.organization_id = organizationIds[0];
      } else if (organizationIds.length > 1) {
        dealParams.organization_ids = organizationIds;
      }

      return this.$store.dispatch(GET_STOCK_DEAL_OPTIONS, dealParams)
        .then((deals) => {
          const dealItems = (deals || []).map((deal) => ({
            id: deal.id,
            label: deal.code ? `${deal.code} — ${deal.name}` : deal.name,
          }));

          this.tableFilters = [
            {
              key: "status",
              label: this.$t("GENERAL.LABELS.STATUS"),
              type: "select",
              "item-value": "id",
              "item-text": "label",
              items: statusItems,
            },
            {
              key: "type",
              label: this.$t("STOCK.INVOICES.TYPE"),
              type: "select",
              "item-value": "id",
              "item-text": "label",
              items: typeItems,
            },
            {
              key: "includes_transport",
              label: this.$t("STOCK.INVOICES.FILTER_TRANSPORT"),
              type: "select",
              "item-value": "id",
              "item-text": "label",
              items: [
                { id: "yes", label: this.$t("STOCK.INVOICES.TRANSPORT_YES") },
                { id: "no", label: this.$t("STOCK.INVOICES.TRANSPORT_NO") },
              ],
            },
            {
              key: "overdue",
              label: this.$t("STOCK.INVOICES.FILTER_OVERDUE"),
              type: "select",
              "item-value": "id",
              "item-text": "label",
              items: [
                { id: "yes", label: this.$t("STOCK.INVOICES.OVERDUE") },
              ],
            },
            {
              key: "reminder_due",
              label: this.$t("STOCK.INVOICES.FILTER_REMINDER_DUE"),
              type: "select",
              "item-value": "id",
              "item-text": "label",
              items: [
                { id: "yes", label: this.$t("STOCK.INVOICES.REMINDER_DUE") },
              ],
            },
            {
              key: "deal_id",
              label: this.$t("STOCK.PURCHASE_ORDERS.DEAL"),
              type: "select",
              "item-value": "id",
              "item-text": "label",
              items: dealItems,
            },
          ];
        })
        .catch(() => {
          this.tableFilters = [
            {
              key: "status",
              label: this.$t("GENERAL.LABELS.STATUS"),
              type: "select",
              "item-value": "id",
              "item-text": "label",
              items: statusItems,
            },
            {
              key: "overdue",
              label: this.$t("STOCK.INVOICES.FILTER_OVERDUE"),
              type: "select",
              "item-value": "id",
              "item-text": "label",
              items: [
                { id: "yes", label: this.$t("STOCK.INVOICES.OVERDUE") },
              ],
            },
            {
              key: "reminder_due",
              label: this.$t("STOCK.INVOICES.FILTER_REMINDER_DUE"),
              type: "select",
              "item-value": "id",
              "item-text": "label",
              items: [
                { id: "yes", label: this.$t("STOCK.INVOICES.REMINDER_DUE") },
              ],
            },
          ];
        });
    },
    reloadTable() {
      this.$nextTick(() => {
        this.$refs["stock-invoices-table"]?.fetchServerData();
      });
    },
    clearCreateValidationError(field) {
      if (this.createValidationErrors[field]) {
        this.$delete(this.createValidationErrors, field);
      }
    },
    async createManualInvoice() {
      this.isCreating = true;
      this.createValidationErrors = {};
      try {
        const params = {
          organization_id: this.createForm.organization_id,
          direction: this.createForm.direction,
          type: this.createForm.type,
          company_id: this.createForm.company_id,
        };
        if (this.createForm.direction === "incoming") {
          params.external_number = (this.createForm.external_number || "").trim();
        }
        const data = await this.$store.dispatch(CREATE_STOCK_INVOICE, params);
        this.showCreateDialog = false;
        this.createForm.external_number = "";
        this.$router.push({ name: "stock-invoices-view", params: { id: data.id } });
      } catch (errors) {
        this.createValidationErrors = (errors && errors.errors) || {};
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isCreating = false;
      }
    },
    companyLabel(row) {
      const company = row.company;
      if (!company) return "-";
      return company.label || company.name || "-";
    },
    deliveryNotesOf(row) {
      return Array.isArray(row.delivery_notes) ? row.delivery_notes : [];
    },
    organizationLabel(row) {
      const org = row.organization;
      if (!org) return "-";
      return org.name || org.code || "-";
    },
    statusLabel(status) {
      const key = `STOCK.STATUSES.INVOICE.${(status || "").toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : status;
    },
    typeLabel(type) {
      const key = `STOCK.INVOICES.TYPES.${(type || "").toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : type;
    },
    directionLabel(direction) {
      const key = `STOCK.INVOICES.DIRECTIONS.${(direction || "").toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : direction;
    },
    directionColor(direction) {
      return direction === "outgoing" ? "teal darken-1" : "deep-purple darken-1";
    },
    formatMoney(value, currency) {
      return `${Number(value || 0).toFixed(2)} ${currency || ""}`.trim();
    },
    formatDueDate(value) {
      if (!value) {
        return "-";
      }
      const [year, month, day] = String(value).split("-");
      if (!year || !month || !day) {
        return value;
      }
      return `${day}.${month}.${year}`;
    },
    daysUntilDue(row) {
      if (row.days_until_due !== null && row.days_until_due !== undefined) {
        return Number(row.days_until_due);
      }
      const anchor = row.due_date || row.issue_date;
      if (!anchor) {
        return row.due_date ? null : (this.hasOpenInvoiceBalance(row) ? -1 : null);
      }
      const due = new Date(`${String(anchor).slice(0, 10)}T00:00:00`);
      if (Number.isNaN(due.getTime())) {
        return row.due_date ? null : -1;
      }
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const days = Math.round((due.getTime() - today.getTime()) / 86400000);
      if (!row.due_date) {
        return this.hasOpenInvoiceBalance(row) ? Math.min(days, -1) : null;
      }
      return days;
    },
    hasOpenInvoiceBalance(row) {
      if (["paid", "cancelled", "draft"].includes(row.status)) {
        return false;
      }
      const remaining = row.remaining_amount;
      if (remaining !== null && remaining !== undefined) {
        return Number(remaining) > 0;
      }
      const paid = Number(row.paid_amount || 0);
      const gross = Number(row.total_gross || 0);
      if (row.type === "credit_note") {
        return paid < Math.abs(gross);
      }
      return paid < gross;
    },
    isOverdue(row) {
      if (row.is_overdue === true) {
        return true;
      }
      if (row.is_overdue === false) {
        return false;
      }
      if (!this.hasOpenInvoiceBalance(row)) {
        return false;
      }
      if (!row.due_date) {
        return true;
      }
      const days = this.daysUntilDue(row);
      return days !== null && days < 0;
    },
    documentNumberLabel(row) {
      if (row.direction === "incoming") {
        return row.external_number || "-";
      }
      return row.fiscal_number || row.code || "-";
    },
    sourceDocumentLabel(source) {
      if (!source) {
        return "-";
      }
      if (source.direction === "incoming") {
        return source.external_number || source.code || "-";
      }
      return source.fiscal_number || source.code || "-";
    },
  },
};
</script>

<style scoped>
.invoice-dn-links {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  line-height: 1.3;
}
.stock-invoice-stat-card {
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}
.stock-invoice-stat-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}
.stock-invoice-stat-card--draft {
  border-left: 4px solid #9e9e9e !important;
}
.stock-invoice-stat-card--issued {
  border-left: 4px solid #00897b !important;
}
.stock-invoice-stat-card--gross {
  border-left: 4px solid #ffa000 !important;
}
.stock-invoice-direction-card {
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  border-width: 2px !important;
}
.stock-invoice-direction-card--active {
  border-color: #00897b !important;
  box-shadow: 0 2px 12px rgba(0, 137, 123, 0.2);
}
.stock-invoice-direction-card--active-incoming {
  border-color: #5e35b1 !important;
  box-shadow: 0 2px 12px rgba(94, 53, 177, 0.2);
}
.stock-invoice-create-dialog__title {
  background: linear-gradient(to right, #e0f7fa, #ffffff);
}
</style>

<style>
.stock-invoice-status--draft {
  background-color: #757575 !important;
}
.stock-invoice-status--issued {
  background-color: #00897b !important;
}
.stock-invoice-status--partially_paid {
  background-color: #ef6c00 !important;
}
.stock-invoice-status--paid {
  background-color: #2e7d32 !important;
}
.stock-invoice-status--cancelled {
  background-color: #c62828 !important;
}
</style>
